/**
 * Pure release/installation logic shared by the Products, Product detail and Deployments pages.
 * No React, no fetch: everything here is unit-tested in installCounts.test.ts.
 */

/**
 * Guardian states in which a product counts as installed in a tenant. Must match the API's
 * product-card summary (api/src/deploy/releases.ts), or the card and the detail page disagree.
 */
export const INSTALLED_STATES: readonly string[] = ['Installed', 'AwaitingCustomerAzure', 'Live', 'InUse', 'Suspended'];

export function isInstalledState(state: string | null | undefined): boolean {
  return !!state && INSTALLED_STATES.indexOf(state) >= 0;
}

const VERSION_RE = /^\d+(\.\d+)*$/;

/** True for a dotted numeric version ("2.3.6" or "2.3.6.0"); false for empty or garbage. */
export function isVersion(v: string | null | undefined): v is string {
  return typeof v === 'string' && VERSION_RE.test(v.trim());
}

/** Compares dotted versions numerically; negative when a < b. Missing parts count as 0, so "2.3.6" equals "2.3.6.0". */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** Same release, tolerating a missing trailing ".0". Unparseable versions never match anything. */
export function sameVersion(a: string | null | undefined, b: string | null | undefined): boolean {
  return isVersion(a) && isVersion(b) && compareVersions(a, b) === 0;
}

/** Reads a field that Guardian may send as camelCase or PascalCase (camelCase wins). */
export function pickField(record: Record<string, unknown>, name: string): unknown {
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  const camel = record[name];
  return camel !== undefined && camel !== null ? camel : record[pascal];
}

export interface InstallVersionInfo {
  /** What the tenant actually runs: runningVersion, else verifiedVersion, else Version. */
  runningVersion: string | null;
  verifiedVersion: string | null;
  latestVersion: string | null;
  outdated: boolean | null;
  runningWithdrawn: boolean;
  belowMinimum: boolean;
  /** How many releases behind current, when Guardian sends it. */
  behind: number | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && isFinite(v) ? v : null);

/** Release-management fields of one Guardian installation row. */
export function readInstallVersions(i: Record<string, unknown>): InstallVersionInfo {
  const verifiedVersion = str(pickField(i, 'verifiedVersion'));
  return {
    runningVersion: str(pickField(i, 'runningVersion')) ?? verifiedVersion ?? str(pickField(i, 'version')),
    verifiedVersion,
    latestVersion: str(pickField(i, 'latestVersion')),
    outdated: bool(pickField(i, 'outdated')),
    runningWithdrawn: pickField(i, 'runningWithdrawn') === true,
    belowMinimum: pickField(i, 'belowMinimum') === true,
    behind: num(pickField(i, 'behind')),
  };
}

/** The minimum an installation needs for counting and badges. */
export interface InstallVersionView {
  state: string;
  /** Running version ('' when unknown). */
  version: string;
  latestVersion: string | null;
  outdated: boolean | null;
  runningWithdrawn: boolean;
}

/** Installations in an installed state that run `version` ("2.3.6" counts as "2.3.6.0"). */
export function countInstalledOn(installs: readonly InstallVersionView[], version: string): number {
  return installs.filter((i) => isInstalledState(i.state) && sameVersion(i.version, version)).length;
}

export type InstallBadge =
  | { kind: 'withdrawn' }
  /** target is null when Guardian flagged the install outdated but no target version is known. */
  | { kind: 'update'; target: string | null };

/**
 * Which version badge an installation shows.
 * - Running a withdrawn build (Guardian's flag, or the running version is in `withdrawnVersions`) wins.
 * - Otherwise a computed comparison against the target beats Guardian's `outdated` flag, as in the API.
 * - The flag is used only when the running version or the target cannot be compared.
 * `fallbackTarget` is the product's current release, used when Guardian sends no latestVersion.
 */
export function installBadge(
  i: InstallVersionView,
  fallbackTarget: string | null,
  withdrawnVersions: readonly string[] = [],
): InstallBadge | null {
  if (i.runningWithdrawn || withdrawnVersions.some((w) => sameVersion(w, i.version))) return { kind: 'withdrawn' };
  const target = i.latestVersion ?? fallbackTarget;
  if (isVersion(i.version) && isVersion(target)) {
    return compareVersions(i.version, target) < 0 ? { kind: 'update', target } : null;
  }
  return i.outdated === true ? { kind: 'update', target: target ?? null } : null;
}

/** The site URL when it is safe to open in a new tab (https only), else null. */
export function safeSiteUrl(url: string | null | undefined): string | null {
  return typeof url === 'string' && /^https:\/\//i.test(url.trim()) ? url.trim() : null;
}
